import { observer } from 'mobx-react'
import React, { useEffect, useRef, useState } from 'react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

// evertel
import { useService } from '@evertel/di'
import { DeviceTokenService } from '@evertel/session/DeviceTokenService'
import { SessionService } from '@evertel/session/SessionService'
import { AuthenticationController } from '@evertel/shared/feature-authentication-auth0'
import { CurrentUserController } from '@evertel/blue-user'
import { Button, Card, CardBody, FormInput, Icon, Row, useUI } from '@evertel/web/ui'
import { RegistrationData } from '../RegistrationStepperAuthorized'


interface Step3SMSAuthenticationProps {
    registrationData: RegistrationData & Partial<{ parsedPhoneNumber: string}>
    onStepComplete: () => void
}

const Step3SMSAuthentication: React.FC<Step3SMSAuthenticationProps> = observer(({
    registrationData,
    onStepComplete
}) => {

    const sessionService = useService(SessionService)
    const deviceTokenService = useService(DeviceTokenService, [])
    const authenticationController = useService(AuthenticationController)
    const currentUserController = useService(CurrentUserController)

    const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined)
    const [firstEntry, setFirstEntry] = useState<boolean>(true)
    const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false)
    const [code, setCode] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [smsSent, setSmsSent] = useState(false)

    const autoSubmitCountRef = useRef(0)

    const { addToast } = useUI()

    useEffect(() => {
        if (currentUserController.user) {
            setPhoneNumber(parsePhoneNumberFromString(String(currentUserController.user?.phoneNumber), 'US')?.formatNational())
        }
    }, [currentUserController.user])

    useEffect(() => {
        if (code?.length === 6 && autoSubmitCountRef.current < 1) {
            autoSubmitCountRef.current++
            handleConfirmSMS()
        }
    }, [code])

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatPhoneNumber(e.target.value)
        setPhoneNumber(formatted)
    }
    const formatPhoneNumber = (value) => {
        const numbers = value.replace(/\D/g, '')
        const limited = numbers.slice(0, 10)
        if (limited.length <= 3) {
            return limited
        } else if (limited.length <= 6) {
            return `${limited.slice(0, 3)}-${limited.slice(3)}`
        } else {
            return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`
        }
    }

    const onChangeCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        const numericValue = value.replace(/\D/g, '').slice(0, 6)
        setCode(numericValue)
    }

    const handleSendSMS = async () => {

        if (!deviceTokenService.token) return

        const parsedNumber = parsePhoneNumberFromString(String(phoneNumber), 'US')

        if (!parsedNumber?.isValid()) {
            addToast ({
                title: 'Error',
                color: 'danger',
                message: 'Invalid phone number'
            })

            return
        }
        setIsLoading(true)

        try {
            await authenticationController.getAuthenticators(sessionService.currentUserId)
            const existingSMSAuth = authenticationController.authenticators?.find(a => a.type === 'sms')

            //Already did an enrollment, we need to delete it and create a new one
            if (existingSMSAuth) {
                const authenticatorLastDigits = existingSMSAuth?.name.slice(-4)
                if ((parsedNumber.number === registrationData.parsedPhoneNumber) && (authenticatorLastDigits === registrationData.parsedPhoneNumber.slice(-4))) {
                    //the finally will properly set states
                    return

                } else {
                    await authenticationController.deleteAuthenticator(
                        sessionService.currentUserId,
                        existingSMSAuth.id,
                        currentUserController.user.email as string,
                        registrationData.password,
                        deviceTokenService.token
                    )
                }
            }

            await currentUserController.update({
                phoneNumber: parsedNumber.number
            })

            registrationData.parsedPhoneNumber = parsedNumber.number

            const body = {
                username: currentUserController.user.email,
                password: registrationData.password,
                phoneNumber: parsedNumber.number,
                deviceToken: deviceTokenService.token
            }
            const response = await authenticationController.enrollMFA(body)
            authenticationController.setMFAParams(response?.access_token, response?.oob_code)
            autoSubmitCountRef.current = 0 // reset auto-submit

        } catch (e: any) {
            console.error(e)
            addToast({
                title: 'Error',
                color: 'danger',
                message: e.message
            })
        } finally {
            setSmsSent(true)
            setIsLoading(false)
        }
    }

    const handleConfirmSMS = async () => {
        setIsLoading(true)
        const { mfaToken, oobCode } = authenticationController
        if (!mfaToken || !oobCode) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: 'Missing tokens'
            })
            setIsLoading(false)
            return
        }
        try {
            const enableSMSParams = {
                mfaToken,
                oobCode,
                bindingCode: code,
                deviceToken: deviceTokenService.token as string
            }
            await authenticationController.verifyChallenge(enableSMSParams)

            // refresh authenticators list
            const userId = sessionService.currentUserId
            if (userId) await authenticationController.getAuthenticators(userId)

            addToast({
                color: 'success',
                message: 'Enrollment Successful'
            })
            onStepComplete()
        } catch (e: any) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: e.message
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleSkipSMS = () => {
        onStepComplete()
    }

    const handleGoBack = () => {
        setSmsSent(false)
        setCode('')
    }

    useEffect(() => {
        const parsedNumber = parsePhoneNumberFromString(String(phoneNumber), 'US')

        if ((parsedNumber?.nationalNumber?.length ?? 0) >= 10) setFirstEntry(false)
        if ((parsedNumber?.nationalNumber?.length ?? 0) === 0) setFirstEntry(true)

        setIsPhoneValid(!!parsedNumber?.isValid())
    }, [phoneNumber])

    return (
        <div className="app fadeIn animated">
            <Row align="center" valign="center" className="p-4" style={{ minHeight: '100%' }}>
                <Card>
                    <CardBody className="p-5">
                        <h1>Enable faster login with SMS</h1>
                        {!smsSent ? (
                            <>
                                <p>We will send a 6-digit security code to your mobile phone via text message.</p>
                                <FormInput
                                    name="phoneNumber"
                                    value={phoneNumber}
                                    leftIcon="phone"
                                    label="Phone number (Optional)"
                                    placeholder="XXX-XXX-XXXX"
                                    disabled={isLoading}
                                    onChange={handlePhoneNumberChange}
                                    className="mt-4 mb-3"
                                    autoComplete="tel"
                                    inputMode="tel"
                                    error={(!isPhoneValid && !firstEntry) ? 'Invalid Phone Number' : undefined}
                                />
                                <i>
                                    <small>We'll only use this number to send you verification codes</small>
                                </i>
                                <Button
                                    className="mt-3 w-100"
                                    color="primary"
                                    onClick={handleSendSMS}
                                    disabled={isLoading || !isPhoneValid}
                                    loading={isLoading}
                                >
                                    Enable SMS Login
                                </Button>

                                <div className="text-center mt-2">
                                    <Button
                                        color="link"
                                        onClick={handleSkipSMS}
                                        disabled={isLoading}
                                    >
                                        Use email instead
                                        <Icon name="angle-right" className="ms-1" />
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p>Enter the code that we sent via SMS to your phone:</p>
                                <div className="text-center text-primary mb-3">
                                    <div className="mb-2">
                                        <Icon name="mobile-android-alt" className="me-2" />
                                        {phoneNumber}
                                    </div>

                                    <FormInput
                                        name="code"
                                        type="text"
                                        value={code}
                                        onChange={onChangeCode}
                                        placeholder="••••••"
                                        inputClassName="text-center"
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                        inputMode="numeric"
                                        autoFocus
                                        style={{
                                            letterSpacing: 10,
                                            fontSize: 'x-large',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                </div>
                                <Button
                                    className="mt-3 w-100"
                                    color="primary"
                                    onClick={handleConfirmSMS}
                                    disabled={code.length !== 6 || isLoading}
                                    loading={isLoading}
                                >
                                    Verify Code
                                </Button>

                                <div className="text-center mt-2">
                                    <Button
                                        color="link"
                                        onClick={handleGoBack}
                                        disabled={isLoading}
                                    >
                                        <Icon name="angle-left" className="me-1" />
                                        Back
                                    </Button>
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </Row>
        </div>
    )
})
export { Step3SMSAuthentication }
